import React from "react";
import { createRoot } from "react-dom/client";
import * as THREE from "three";
import { createActor } from "xstate";
import { App } from "./ui/App.js";
import { SceneManager } from "./engine/renderer/SceneManager.js";
import { CameraController } from "./engine/camera/CameraController.js";
import { buildDungeon } from "./engine/renderer/DungeonBuilder.js";
import { getOrLoadMap } from "./engine/loaders/TiledLoader.js";
import { GridMap } from "./engine/grid/GridMap.js";
import { cellOccupancyStore } from "./engine/grid/cellOccupancyStore.js";
import { Direction } from "./engine/types/grid.js";
import type { Position } from "./engine/types/grid.js";
import { InputHandler, getActiveInputHandler } from "./engine/input/InputHandler.js";
import { CombatManager } from "./engine/combat/CombatManager.js";
import { setCombatManager } from "./engine/combat/combatManagerRef.js";
import { SpriteManager } from "./engine/sprites/SpriteManager.js";
import { SpellVfx } from "./engine/renderer/SpellVfx.js";
import { setRendererContext } from "./ui/rendererContext.js";
import { narrativeManager } from "./engine/narrative/NarrativeManager.js";
import { EventBus } from "./engine/EventBus.js";
import { initFeedBus } from "./engine/Feed.js";
import { SneakState, SNEAK_SPEED_MULTIPLIER } from "./engine/party/SneakState.js";
import { WorldStore } from "./store/WorldStore.js";
import { PartyStore } from "./store/PartyStore.js";
import { ShopStore } from "./store/ShopStore.js";
import { playerMachine } from "./machines/playerMachine.js";
import { enemyMachine } from "./machines/enemyMachine.js";
import { gameActor } from "./ui/hooks/useGameMachine.js";

if (import.meta.env.DEV) {
  gameActor.subscribe((snap) => {
    console.log(`[bk] gameActor → ${JSON.stringify(snap.value)}`);
  });
}

// Difficulty boot params — `?difficulty=peace|story|normal|hard|brutal`
// (or 0–4), plus `?peace=1` as shorthand for the Peace floor: map
// walkthroughs and Ink-knot checking with every enemy pacified. Also a
// MainMenu slider; see src/engine/combat/difficulty.ts.
{
  const params = new URLSearchParams(window.location.search);
  const wanted = params.get("difficulty");
  const parsed = wanted === null ? null : parseDifficulty(wanted);
  if (parsed !== null) setDifficulty(parsed);
  if (params.get("peace") === "1") setDifficulty("peace");
}
import { selectIsWorldTicking, selectIsWorldFrozen } from "./machines/gameMachine.js";
import { cooldownClock } from "./engine/combat/CooldownClock.js";
import { parseDifficulty, setDifficulty } from "./engine/combat/difficulty.js";
import { loadShopsForNpcs } from "./engine/inventory/loadShops.js";
import { applyConditionTo } from "./engine/combat/conditionRuntime.js";
import type { ConditionId } from "./engine/types/entity.js";
import { toNDC, pickGroundCell } from "./engine/renderer/Raycaster.js";
import { InteractionDispatcher } from "./engine/renderer/InteractionDispatcher.js";
import { SpellTargetingDispatcher } from "./engine/spells/SpellTargetingDispatcher.js";
import { setSpellTargeting } from "./engine/spells/spellTargetingRef.js";
import { setGridMap } from "./engine/grid/gridMapRef.js";
import { loadSpell } from "./engine/loaders/SpellLoader.js";
import { DoorRenderer } from "./engine/renderer/DoorRenderer.js";
import {
  FloorDecalManager,
  type FloorDecalDescriptor,
} from "./engine/renderer/FloorDecalManager.js";
import {
  PropManager,
  type PropDescriptor,
} from "./engine/renderer/PropManager.js";
import { SecretDoorPerception, highestPassivePerceiver } from "./engine/world/SecretDoorPerception.js";
import { TrapPerception } from "./engine/world/TrapPerception.js";
import { LightManager, type LightDescriptor } from "./engine/world/LightManager.js";
import { spawnerRegistry } from "./engine/spawners/SpawnerRegistry.js";
import { triggerTrap, attemptDisarm, disarmOutcome } from "./engine/traps/TrapSystem.js";
import { chebyshev } from "./engine/grid/distance.js";
import { hasLineOfSight } from "./engine/grid/LineOfSight.js";
import { attemptPickpocket } from "./engine/npc/pickpocket.js";
import { pushFeed } from "./engine/Feed.js";
import { LootTableRegistry } from "./engine/inventory/LootTableRegistry.js";
import { LootPileStore } from "./store/LootPileStore.js";
import { LootPileRenderer } from "./engine/renderer/LootPileRenderer.js";
import { loadItem } from "./engine/loaders/ItemLoader.js";
import type { LootPile } from "./engine/inventory/LootPile.js";
import type { Item, ItemStack } from "./engine/types/items.js";
import { GOLD_ITEM_ID, makeGoldItem } from "./engine/inventory/goldItem.js";
import { createLootTableResolver } from "./engine/inventory/lootResolver.js";
import { checkCampEligibility } from "./engine/party/campEligibility.js";
import { createLevelUpWatcher } from "./engine/world/levelUpWatcher.js";
import { dataRegistry } from "./engine/loaders/DataRegistry.js";
import type { SRDClass } from "./engine/types/srd.js";
import { KeyringStore } from "./store/KeyringStore.js";
import { InventoryStore } from "./store/InventoryStore.js";
import { isSpellbook } from "./engine/party/equipment.js";
import { openContainer } from "./engine/inventory/containerOpen.js";
import type {
  TrapMetadata,
  TeleporterMetadata,
  StairsDownMetadata,
  StairsUpMetadata,
  TrapTeleporterMetadata,
  InteractMetadata,
} from "./engine/types/tiled.js";
import {
  routeInteractEffect,
  type InteractRouterResult,
} from "./engine/world/InteractEffectRouter.js";
import type { Character, EnemyFaction, MonsterEntity, NPCEntity } from "./engine/types/entity.js";
import { DEFAULT_SIGHT, DEFAULT_HEARING, abilityModifier } from "./engine/types/entity.js";
import { buildMonsterEntity } from "./engine/loaders/buildMonsterEntity.js";
import { noiseBroadcast, NOISE_RADIUS } from "./engine/perception/NoiseBroadcast.js";
import { loadApiKey } from "./engine/llm/apiKeyStore.js";
import { ClaudeClient } from "./engine/llm/ClaudeClient.js";
import { setLLMClient } from "./engine/llm/LLMClient.js";
import { getActiveAdventure, introKnot, worldTextureUrl } from "./engine/adventure/ActiveAdventure.js";
import { consumePendingIntro } from "./engine/narrative/introGate.js";
import { audio } from "./engine/audio/AudioManager.js";
import type { AmbientMood } from "./engine/audio/synthPatches.js";
import {
  initRetroPresentation,
  loadRetroPreference,
} from "./engine/renderer/RetroPresentation.js";
import { startAuthoringLiveReload } from "./engine/adventure/authoringLiveReload.js";
import { createAutosaver } from "./engine/persistence/Autosaver.js";
import { createQuicksaveHotkeys } from "./engine/persistence/QuicksaveHotkeys.js";
import { isSaveBlocked, getSaveBlockReason } from "./engine/persistence/saveBlocker.js";
import { setSaveInputProvider } from "./engine/persistence/saveInputRef.js";
import type { SaveSlotInput } from "./engine/persistence/PersistenceManager.js";

// ── 1. Rehydrate LLM client from stored API key (L02) ───────────────────────
const _savedKey = loadApiKey();
if (_savedKey) setLLMClient(new ClaudeClient(_savedKey));

// ── 2. Mount the React UI overlay ──────────────────────────────────────────
const uiRoot = document.getElementById("ui-root");
if (!uiRoot) throw new Error("Missing #ui-root element in index.html");
createRoot(uiRoot).render(<App />);

// Subscribe the feed formatter to awareness events (E11) once at boot.
initFeedBus();

// I16: subscribe the keyring to inventory:give_item once at boot so the
// lock menu can check whether a matching key has ever been received.
KeyringStore.init();

// W09 glue: XP awards → level-ups. The watcher needs a synchronous class
// lookup, so class records are warmed into this cache when a map loads
// (chargen limits the roster to a handful of slugs per adventure).
//
// A party reused across adventures can carry a class the destination's data
// tree doesn't define (an Exit Velocity psion walking bkcrawl-mvp, say).
// Without a record, the watcher would skip that member forever and XP would
// accrue with no level-ups — so a miss warms a generic fallback instead:
// d8-ish HP progression, no spellcasting. The level-up slot merge only ever
// raises caps, so an imported caster keeps the slots they arrived with.
function genericClassFallback(slug: string): SRDClass {
  return {
    index: slug,
    name: slug.charAt(0).toUpperCase() + slug.slice(1),
    hit_die: 8,
    hp_per_level: 5,
    saving_throws: [],
    skill_choices: { count: 0, from: [] },
    armor_proficiencies: [],
    weapon_proficiencies: [],
  };
}
const classRecordCache = new Map<string, SRDClass | null>();
function warmClassRecordCache(): void {
  for (const member of PartyStore.getMembers()) {
    const slug = member.characterClass.toLowerCase();
    if (classRecordCache.has(slug)) continue;
    classRecordCache.set(slug, null);
    void dataRegistry.getClass(slug).then((record) => {
      if (!record) {
        console.warn(
          `[levelUp] no class record for "${slug}" in this adventure — using generic fallback progression`,
        );
      }
      classRecordCache.set(slug, record ?? genericClassFallback(slug));
    });
  }
}
createLevelUpWatcher({
  getMembers: () => PartyStore.getMembers(),
  getClassData: (characterClass) => classRecordCache.get(characterClass.toLowerCase()) ?? null,
  updateMember: (instanceId, patch) => {
    PartyStore.updateMember(instanceId, patch);
    EventBus.emit("ui:party_update", {});
  },
});

// ── 2. Boot the Three.js renderer ──────────────────────────────────────────
const canvas = document.getElementById("game");
if (!(canvas instanceof HTMLCanvasElement)) {
  throw new Error("Missing <canvas id='game'> in index.html");
}
const sceneManager = new SceneManager(canvas);
const cameraController = new CameraController(sceneManager.camera);
setRendererContext({ camera: sceneManager.camera, canvas });

// GF5: retro presentation (chunky internal res + scanline/vignette overlay).
initRetroPresentation(sceneManager, canvas, loadRetroPreference(window.location.search));

// GF3: browsers keep audio suspended until a user gesture — unlock on the
// first input and keep the listeners as cheap resume-after-suspend hooks.
const unlockAudio = (): void => {
  audio.unlock();
};
window.addEventListener("pointerdown", unlockAudio, { passive: true });
window.addEventListener("keydown", unlockAudio);

// Halve camera tween speed while sneaking (E03).
SneakState.subscribe((sneaking) => {
  cameraController.setTweenDurationMultiplier(sneaking ? SNEAK_SPEED_MULTIPLIER : 1);
});

// ── 3. Ambient + point lights (E5) ─────────────────────────────────────────
const ambientLight = new THREE.AmbientLight(0x404058, 1.3);
sceneManager.scene.add(ambientLight);

// GF1: tighter falloff than the original 16 — the dark beyond the torch
// radius is where the genre's dread lives (distance fog finishes the job).
const TORCH_INTENSITY = 4.0;
const pointLight = new THREE.PointLight(0xffeecc, TORCH_INTENSITY, 10);
sceneManager.scene.add(pointLight);

// Per-map authored ceiling lights (W22). Owned by `LightManager`, which is
// created fresh in `loadAndBuildMap` from each map's `type: "light"` Tiled
// objects and disposed when a new map loads. The old `setMapLights`
// hardcode lived here before W22.
let lightManager: LightManager | null = null;

// ── 3b. Outdoor sunlight (W-series follow-up) ──────────────────────────────
// Added to the scene unconditionally but toggled off when the current map
// has no cells marked `noCeiling`. A directional "sun" delivers the hard
// overhead key, and a hemisphere light fills with sky vs. ground bounce.
// Colors match the sky-blue background set below.
const SKY_COLOR = 0x87b3d8;
const GROUND_BOUNCE = 0x4a6a3a;
const DUNGEON_BG = 0x000000;
const sunLight = new THREE.DirectionalLight(0xfff1cc, 2.5);
sunLight.position.set(0.3, 1, 0.4);
sunLight.visible = false;
sceneManager.scene.add(sunLight);
const skyFill = new THREE.HemisphereLight(SKY_COLOR, GROUND_BOUNCE, 0.8);
skyFill.visible = false;
sceneManager.scene.add(skyFill);

// Authored sky panorama (equirectangular, `world/textures/sets/<key>/sky/
// albedo.png`, adventure default at `world/textures/sky/albedo.png`). The
// camera never pitches, so a single 2:1-ish panorama on scene.background
// replaces a cubemap outright; the flat SKY_COLOR stays as the fallback
// while loading and when no sky is authored. Token guards the async load
// against a map change racing the fetch.
let skyTexture: THREE.Texture | null = null;
let skyLoadToken = 0;

function setOutdoorLighting(enabled: boolean, setKey?: string): void {
  sunLight.visible = enabled;
  skyFill.visible = enabled;
  sceneManager.scene.background = new THREE.Color(enabled ? SKY_COLOR : DUNGEON_BG);
  // GF1: distance fog. Indoors fades to black over ~9 cells (EOB-style
  // "the corridor disappears into dark"); outdoors it's a sky-colored
  // haze so the horizon blends instead of going ominous.
  sceneManager.scene.fog = enabled
    ? new THREE.Fog(SKY_COLOR, 7, 20)
    : new THREE.Fog(DUNGEON_BG, 2.5, 9.5);
  skyLoadToken++;
  skyTexture?.dispose();
  skyTexture = null;
  if (!enabled) return;

  const token = skyLoadToken;
  const loader = new THREE.TextureLoader();
  const apply = (tex: THREE.Texture): void => {
    if (token !== skyLoadToken) {
      tex.dispose();
      return;
    }
    tex.mapping = THREE.EquirectangularReflectionMapping;
    // Authored PNGs are sRGB — tag them or the renderer gamma-lifts on output.
    tex.colorSpace = THREE.SRGBColorSpace;
    skyTexture = tex;
    sceneManager.scene.background = tex;
  };
  const defaultUrl = worldTextureUrl("sky", "albedo");
  if (setKey) {
    loader.load(worldTextureUrl("sky", "albedo", setKey), apply, undefined, () => {
      loader.load(defaultUrl, apply, undefined, () => undefined);
    });
  } else {
    loader.load(defaultUrl, apply, undefined, () => undefined);
  }
}

// ── 4. Mutable refs replaced on level load ──────────────────────────────────
let inputHandler: InputHandler | null = null;
let combatManager: CombatManager | null = null;
let spriteManager: SpriteManager | null = null;
const spellVfx = new SpellVfx(sceneManager.scene, sceneManager.camera, () => spriteManager);
let doorRenderer: DoorRenderer | null = null;
let floorDecalManager: FloorDecalManager | null = null;
let propManager: PropManager | null = null;
/** Player's current grid cell — updated from the InputHandler's move callback. */
let currentPlayerCell: { x: number; z: number } = { x: 0, z: 0 };

// Dev-only navigation probe for scripted playtests (Playwright smokes):
// exposes the party's live cell + facing so a driver can re-path each step
// instead of dead-reckoning keystrokes. Never present in production builds.
if (import.meta.env.DEV) {
  (window as { __bkNav?: () => unknown }).__bkNav = () => {
    const h = getActiveInputHandler();
    return h
      ? { pos: h.getPosition(), facing: h.getFacing() }
      : { pos: { ...currentPlayerCell }, facing: null };
  };
  (window as { __bkAudio?: () => unknown }).__bkAudio = () => ({ running: audio.isRunning() });
}

// boss instanceId → death knot name
const bossDeathKnots = new Map<string, string>();

// I19: item records preloaded for the sync enemy-loot resolver. Keyed by
// item id; filled per map load from the registered loot tables.
const lootItemCache = new Map<string, Item>();

/** Parse Tiled `target_cell` "x,z" strings into a `{x, z}` Position (W18). */
function parseTargetCell(s: string): { x: number; z: number } {
  const [xs, zs] = s.split(",");
  return { x: Number(xs ?? 0) | 0, z: Number(zs ?? 0) | 0 };
}

/**
 * GF3 — ambient bed per map. Keyed off adventure + map-id prefix; new
 * adventures fall back to the dungeon drone. (When ambience becomes
 * authored content this moves into the manifest — see specs/game-feel.md.)
 */
function ambientMoodForMap(mapId: string): AmbientMood {
  const adv = getActiveAdventure().manifest.id;
  if (adv === "exit-velocity") {
    if (mapId.startsWith("station_")) return "station";
    if (mapId.startsWith("fold_")) return "fold";
    return "wind"; // the island floors
  }
  if (mapId === "high_pass" || mapId === "forest") return "wind";
  return "dungeon";
}

// ── 5. Game loop ────────────────────────────────────────────────────────────
let lastTime = performance.now();

function loop(now: number): void {
  const delta = (now - lastTime) / 1000;
  lastTime = now;

  cameraController.tick(delta);
  if (selectIsWorldTicking(gameActor.getSnapshot())) {
    cooldownClock.tick(delta);
    combatManager?.tick(delta);
    PartyStore.addPlaytime(delta);
  }
  spriteManager?.tick(delta);
  spellVfx.tick(delta);

  // Keep point light at camera position
  const camPos = sceneManager.camera.position;
  pointLight.position.set(camPos.x, camPos.y, camPos.z);

  // GF1: torch flicker — layered sines make cheap, deterministic
  // pseudo-noise (no RNG; presentation only).
  const t = now * 0.001;
  pointLight.intensity =
    TORCH_INTENSITY *
    (1 +
      0.055 * Math.sin(t * 13.7) +
      0.04 * Math.sin(t * 7.1 + 1.7) +
      0.025 * Math.sin(t * 23.3 + 0.4));

  sceneManager.tick();

  requestAnimationFrame(loop);
}

requestAnimationFrame(loop);

// ── 6. Canvas click → interaction dispatcher ─────────────────────────────────
const dispatcher = new InteractionDispatcher(() => {
  const snap = gameActor.getSnapshot();
  if (!selectIsWorldTicking(snap)) return "frozen";
  return snap.matches("combat") ? "combat" : "exploring";
});

// N08b: per-map index of NPC pickpocket metadata, populated by loadAndBuildMap.
const npcPickpocketInfo = new Map<string, { dc: number; table?: string }>();

// E16: per-map NPC dialogue metadata for the bump-to-talk handler. Keyed
// by instanceId so a `world:bump_entity` event resolves to the same
// (inkKnot, portrait) pair the click dispatcher would fire.
const npcDialogueInfo = new Map<string, { inkKnot: string; portrait?: string }>();

// I15a: per-map index of containers by `container_id`. Populated during the
// cell scan in `loadAndBuildMap`; cleared on every map load. I16 extends
// the value with `lock_dc` + `key_item_id` so the lock-menu event can
// surface those to the UI without a separate lookup.
const containersById = new Map<
  string,
  {
    cell: { x: number; z: number };
    locked: boolean;
    loot_table?: string;
    lock_dc?: number;
    key_item_id?: string;
  }
>();

// I16: per-map index of doors whose Tiled metadata declares a lock.
// Doors without a lock never appear in this map — the door-toggle rule
// just looks up `doorsById.get(doorId)?.locked` and defaults to
// "unlocked, just toggle".
const doorsById = new Map<
  string,
  { locked: boolean; lock_dc?: number; key_item_id?: string }
>();

// W21b: per-map indexes of interact objects (levers/buttons/plates).
// `interactsById` drives the click rule; `stepInteractsByCell` drives
// the pressure-plate step path. Both cleared on every map load.
const interactsById = new Map<
  string,
  { cell: { x: number; z: number }; meta: InteractMetadata }
>();
const stepInteractsByCell = new Map<string, { meta: InteractMetadata }>();

// W11c: per-map index of traps by trapId (`trap-<objectId>`), populated
// during the cell scan. Drives the disarm click rule and the spotted-a-trap
// feed line; cleared on every map load.
const trapInfoById = new Map<string, { meta: TrapMetadata; cell: { x: number; z: number } }>();
/** Set at the end of every `loadAndBuildMap` so the router can resolve teleporters. */
let currentDungeonMap: import("./engine/types/grid.js").DungeonMap | null = null;

/** Monotonic build counter backing `loadAndBuildMap`'s stale-build guard. */
let mapBuildGeneration = 0;

/**
 * W21b — turn a router result into the concrete engine action + emit the
 * shared `world:interact_activated` event so PropManager state-swaps. One
 * helper, two call sites (click rule below, step path in the move hook).
 */
function dispatchInteractDirective(
  result: InteractRouterResult,
  cell: { x: number; z: number },
  interactId: string,
  mapId: string,
): void {
  EventBus.emit("world:interact_activated", {
    interactId,
    cell,
    effectKind: result.kind,
  });
  switch (result.kind) {
    case "teleport": {
      const sameMap = !result.targetMapId || result.targetMapId === mapId;
      EventBus.emit("world:teleporter_used", {
        teleporterId: result.teleporterId,
        fromCell: cell,
        targetMap: result.targetMapId,
        targetCell: result.targetCell,
      });
      if (sameMap) {
        cameraController.snapTo(
          result.targetCell.x,
          result.targetCell.z,
          Direction.South,
        );
        currentPlayerCell = result.targetCell;
        combatManager?.updatePlayerPosition(result.targetCell);
        EventBus.emit("ui:player_moved", { position: result.targetCell });
        WorldStore.markExplored(result.targetCell);
      } else {
        gameActor.send({
          type: "LOAD_MAP",
          mapId: result.targetMapId,
          targetCell: result.targetCell,
        });
      }
      return;
    }
    case "open_door":
      WorldStore.setDoorOpen(result.doorId, !WorldStore.isDoorOpen(result.doorId));
      return;
    case "fire_trigger":
      // Announce on the trigger channel first so spawners bound to the
      // knot name pop before the dialogue freezes the world (levers and
      // pressure plates can spring ambushes, same as trigger cells).
      EventBus.emit("world:trigger_fired", { triggerId: result.inkKnot });
      narrativeManager.startKnot(result.inkKnot);
      return;
    case "toggle_light":
      // W22: a lever / button / plate targets one or more authored lights.
      // Each id flips membership in `WorldStore.lightsOff`; `LightManager`
      // listens on `world:light_toggled` and flips the PointLight's
      // `visible` flag. Unknown ids are silently ignored — dangling
      // authoring references shouldn't crash a playthrough.
      for (const lightId of result.lightIds) {
        WorldStore.toggleLight(lightId);
      }
      return;
    case "open_camp_stash":
      EventBus.emit("ui:camp_stash_open", {});
      return;
    case "no_op":
      pushFeed("Nothing happens.");
      return;
  }
}

// N08b: sneak-gated pickpocket — wins over the talk rule when the party
// is sneaking and the target NPC has a pickpocketDC. On `caught` we flip
// the NPC hostile; Drops are logged to the feed pending inventory wiring.
dispatcher.register(
  (ctx) =>
    ctx.hit.kind === "npc" &&
    SneakState.isSneaking() &&
    npcPickpocketInfo.has(ctx.hit.instanceId),
  (ctx) => {
    const info = npcPickpocketInfo.get(ctx.hit.instanceId);
    const member = PartyStore.getMembers()[0];
    if (!info || !member) return;
    // Mirrors NarrativeManager: fail closed until the C-series hydrates
    // PlayerCharacter into Character (skillProficiencies, expertise, etc).
    if (!("skillProficiencies" in member)) {
      pushFeed(`${member.name} has no pickpocket training.`);
      return;
    }
    const rogue = member as Character;
    const result = attemptPickpocket({
      rogue,
      npcInstanceId: ctx.hit.instanceId,
      npcPickpocketDC: info.dc,
      ...(info.table !== undefined && { npcPickpocketTable: info.table }),
    });
    if (result.outcome === "caught") {
      combatManager?.setHostile(ctx.hit.instanceId, "pickpocket_caught");
      pushFeed(`${rogue.name} is caught!`);
    } else if (result.outcome === "success") {
      const count = result.drops.length;
      pushFeed(
        count > 0
          ? `${rogue.name} lifts ${count} item${count === 1 ? "" : "s"}.`
          : `${rogue.name} finds nothing of value.`,
      );
    }
  },
);

// W11c: shared trap-detonation path for step-on hits and disarm fumbles.
// Rolls the authored dice against `victim`, applies damage + the rider
// condition, marks the one-shot fired key, and emits the decal-swap event.
function detonateTrap(
  meta: TrapMetadata,
  trapId: string,
  cell: { x: number; z: number },
  victim: { instanceId: string; name: string; hp: number },
  trigger: "step_on" | "search_fail",
): void {
  WorldStore.markTriggerFired(`trap:${meta.objectId}`);
  const result = triggerTrap({ meta, trigger, targets: [victim.instanceId] });
  const newHp = Math.max(0, victim.hp - result.damage);
  PartyStore.updateMember(victim.instanceId, { hp: newHp });
  // Route through the shared damage event so the portrait popup, hurt
  // sound, and any future listeners treat traps like any other hit.
  EventBus.emit("combat:damage_taken", {
    instanceId: victim.instanceId,
    amount: result.damage,
    damageType: result.damageType,
  });
  pushFeed(`${meta.name}! ${victim.name} takes ${result.damage} ${result.damageType} damage.`);
  // EV-E4: the trap's chance-rolled condition finally lands
  // instead of being discarded.
  if (result.conditionId && newHp > 0) {
    applyConditionTo(victim.instanceId, victim.name, result.conditionId as ConditionId, {
      sourceId: trapId,
    });
  }
  EventBus.emit("ui:party_update", {});
  EventBus.emit("world:trap_triggered", { trapId, cell, victimId: victim.instanceId });
}

// W11c: the living member with the best Sleight of Hand bonus does the
// fine-manipulation work. Mirrors the pickpocket rule's fail-closed
// narrowing until PlayerCharacter is fully hydrated into Character.
function bestDisarmer(): Character | null {
  let best: Character | null = null;
  let bestBonus = -Infinity;
  for (const m of PartyStore.getMembers()) {
    if (m.hp <= 0) continue;
    if (!("skillProficiencies" in m)) continue;
    const c = m as Character;
    let bonus = abilityModifier(c.abilities.DEX);
    if (c.skillProficiencies.has("Sleight of Hand")) bonus += c.proficiencyBonus;
    if (c.expertiseSkills.has("Sleight of Hand")) bonus += c.proficiencyBonus;
    if (bonus > bestBonus) {
      bestBonus = bonus;
      best = c;
    }
  }
  return best;
}

// W11c: click a revealed trap decal while adjacent → attempt to disarm.
// Success flips the decal gray; a near miss can be retried; failing by
// DISARM_FUMBLE_MARGIN or more sets the trap off in the disarmer's hands.
dispatcher.register(
  (ctx) =>
    ctx.mode === "exploring" &&
    ctx.hit.kind === "trap" &&
    trapInfoById.has(ctx.hit.instanceId),
  (ctx) => {
    const trapId = ctx.hit.instanceId;
    const info = trapInfoById.get(trapId);
    if (!info) return;
    if (WorldStore.isTrapDisarmed(trapId)) {
      pushFeed(`The ${info.meta.name} is already disarmed.`);
      return;
    }
    if (WorldStore.isTriggerFired(`trap:${info.meta.objectId}`)) {
      pushFeed(`The ${info.meta.name} has already gone off.`);
      return;
    }
    if (chebyshev(currentPlayerCell, info.cell) > 1) {
      pushFeed("Too far away to work on the trap safely.");
      return;
    }
    const disarmer = bestDisarmer();
    if (!disarmer) return;
    const attempt = attemptDisarm({ rogue: disarmer, meta: info.meta });
    const outcome = disarmOutcome(attempt, info.meta.disarm_dc);
    if (outcome === "disarmed") {
      WorldStore.markTrapDisarmed(trapId);
      pushFeed(
        `${disarmer.name} disarms the ${info.meta.name}. (${attempt.check.total} vs DC ${info.meta.disarm_dc})`,
      );
    } else if (outcome === "triggered") {
      pushFeed(`${disarmer.name}'s hand slips —`);
      detonateTrap(info.meta, trapId, info.cell, disarmer, "search_fail");
    } else {
      pushFeed(
        `${disarmer.name} can't find the catch. (${attempt.check.total} vs DC ${info.meta.disarm_dc})`,
      );
    }
  },
);

// Door toggle (W17): any active mode, hit is a door, player is adjacent.
// Adjacency = Manhattan distance ≤ 1 (same cell counts so stepping onto
// an open doorway lets you close it from inside).
dispatcher.register(
  (ctx) => {
    if (ctx.hit.kind !== "door") return false;
    if (ctx.hit.cellX === undefined || ctx.hit.cellZ === undefined) return false;
    const dx = Math.abs(ctx.hit.cellX - currentPlayerCell.x);
    const dz = Math.abs(ctx.hit.cellZ - currentPlayerCell.z);
    return dx + dz <= 1;
  },
  (ctx) => {
    const doorId = ctx.hit.instanceId;
    const lockInfo = doorsById.get(doorId);
    // I16: a locked door that hasn't been unlocked yet routes to the lock
    // menu instead of toggling. Opening a door also re-locks it on close,
    // but the unlock flag persists so the player doesn't need to re-use
    // the key every time.
    if (
      lockInfo?.locked &&
      !WorldStore.isDoorUnlocked(doorId) &&
      !WorldStore.isDoorOpen(doorId)
    ) {
      EventBus.emit("ui:lock_menu_open", {
        targetKind: "door",
        targetId: doorId,
        lockDc: lockInfo.lock_dc ?? 0,
        ...(lockInfo.key_item_id !== undefined && { keyItemId: lockInfo.key_item_id }),
      });
      return;
    }
    WorldStore.setDoorOpen(doorId, !WorldStore.isDoorOpen(doorId));
  },
);

// W22: direct-click on an authored ceiling light toggles it. Adjacency =
// Manhattan distance ≤ 1 so the player walks under / beside the fixture.
// Also covers maps that ship lights without a wall-switch prop.
dispatcher.register(
  (ctx) => {
    if (ctx.hit.kind !== "light") return false;
    if (ctx.hit.cellX === undefined || ctx.hit.cellZ === undefined) return false;
    const dx = Math.abs(ctx.hit.cellX - currentPlayerCell.x);
    const dz = Math.abs(ctx.hit.cellZ - currentPlayerCell.z);
    return dx + dz <= 1;
  },
  (ctx) => {
    WorldStore.toggleLight(ctx.hit.instanceId);
  },
);

// NPC talk: any active mode, hit is an NPC with an ink knot
dispatcher.register(
  (ctx) => ctx.hit.kind === "npc" && !!ctx.hit.inkKnot,
  (ctx) =>
    narrativeManager.startKnot(ctx.hit.inkKnot!, ctx.hit.portrait, ctx.hit.instanceId),
);

// E16: bumping an NPC fires the same ink knot a click would. Look up the
// occupant of the target cell; if it's a known NPC, route through
// `narrativeManager.startKnot` directly. Living enemies in the cell
// fall through silently — combat is click-driven (no bump-to-attack).
EventBus.on("world:bump_entity", ({ targetCell }) => {
  for (const instanceId of cellOccupancyStore.at(targetCell)) {
    const info = npcDialogueInfo.get(instanceId);
    if (info) {
      narrativeManager.startKnot(info.inkKnot, info.portrait, instanceId);
      return;
    }
  }
});

// Enemy attack: any active mode, hit is an enemy
dispatcher.register(
  (ctx) => ctx.hit.kind === "enemy",
  (_ctx) => combatManager?.playerAttack(_ctx.hit.instanceId),
);

// U26: Floor loot pile click → open pickup modal
dispatcher.register(
  (ctx) => ctx.hit.kind === "floor_drop",
  (ctx) => EventBus.emit("ui:loot_pile_open", { pileId: ctx.hit.instanceId }),
);

// I15a: container open — adjacent + unlocked + not-yet-opened → roll the
// loot table once and stash the drop via P16's WorldStore.addFloorDrop.
// Locked containers emit "It's locked." (I16 owns the lock menu). Already
// opened-but-unlooted containers are a silent no-op for now; I15b will
// wire the reopen → modal flow.
dispatcher.register(
  (ctx) => ctx.hit.kind === "container",
  (ctx) => {
    const containerId = ctx.hit.instanceId;
    const info = containersById.get(containerId);
    if (!info) return;
    // I16: an unlock granted via key / lockpick / force treats the container
    // as unlocked for the rest of the session.
    const effectivelyLocked = info.locked && !WorldStore.isContainerUnlocked(containerId);
    const result = openContainer(
      {
        containerId,
        cell: info.cell,
        locked: effectivelyLocked,
        alreadyOpened: WorldStore.isContainerOpened(containerId),
        ...(info.loot_table !== undefined && { lootTableId: info.loot_table }),
      },
      { playerCell: currentPlayerCell, lookupLootTable: LootTableRegistry.lookup },
    );
    switch (result.kind) {
      case "out_of_range":
        return;
      case "locked":
        // I16: hand off to the lock menu instead of a flat "It's locked."
        EventBus.emit("ui:lock_menu_open", {
          targetKind: "container",
          targetId: containerId,
          lockDc: info.lock_dc ?? 0,
          ...(info.key_item_id !== undefined && { keyItemId: info.key_item_id }),
        });
        return;
      case "already_opened":
        if (!WorldStore.isContainerLooted(containerId)) {
          EventBus.emit("ui:container_open", { containerId, cell: info.cell });
        }
        return;
      case "opened": {
        if (result.drops.length > 0) {
          WorldStore.addFloorDrop(containerId, result.cell, result.drops);
        }
        WorldStore.markContainerOpened(containerId, result.cell);
        pushFeed(
          result.drops.length > 0
            ? `You open the container. Inside: ${result.drops.join(", ")}.`
            : "You open the container. It's empty.",
        );
        if (result.drops.length === 0) {
          WorldStore.markContainerLooted(containerId);
        } else {
          EventBus.emit("ui:container_open", { containerId, cell: result.cell });
        }
        return;
      }
    }
  },
);

// W21b: interact-object click — adjacent lever/button/shrine → route the
// authored effect (teleport / open-door / fire-trigger) and emit
// `world:interact_activated` so PropManager swaps to the activated PNG.
// Pressure plates (`trigger_mode === "step"`) never register on the
// raycaster, so this rule only sees click-mode interacts.
dispatcher.register(
  (ctx) => {
    if (ctx.hit.kind !== "interact") return false;
    if (ctx.hit.cellX === undefined || ctx.hit.cellZ === undefined) return false;
    const dx = Math.abs(ctx.hit.cellX - currentPlayerCell.x);
    const dz = Math.abs(ctx.hit.cellZ - currentPlayerCell.z);
    return dx + dz <= 1;
  },
  (ctx) => {
    const interactId = ctx.hit.instanceId;
    const info = interactsById.get(interactId);
    if (!info || !currentDungeonMap) return;
    const result = routeInteractEffect(info.meta.effect, currentDungeonMap);
    dispatchInteractDirective(result, info.cell, interactId, currentDungeonMap.id);
  },
);

// ── S23 — Spell target picker ────────────────────────────────────────────────
// Sits in front of the standard click dispatcher. When a spell is armed:
//   - entity clicks (enemies/allies) are intercepted by `installInto`
//     and committed via `commitOnEntity` for single/touch/multi targeting.
//   - floor-cell clicks fall through here; we raycast the y=0 plane and
//     forward the snapped grid cell to `commitOnCell` for AoE targeting.
const spellTargeting = new SpellTargetingDispatcher((spell, casterId, target) => {
  EventBus.emit("ui:spell_disarmed", { reason: "committed" });
  // Slot level matches the spell base level; upcast UI lands later (S08c).
  combatManager?.castSpell({ casterId, spell, slotLevel: spell.level, target });
});
spellTargeting.installInto(dispatcher);
setSpellTargeting(spellTargeting);

EventBus.on("ui:open_spell_chooser", () => {
  // Opening the chooser cancels any in-flight targeting (e.g. tapping a
  // different spellbook before committing the previous spell).
  if (spellTargeting.isArmed()) {
    spellTargeting.cancel();
    EventBus.emit("ui:spell_disarmed", { reason: "cancelled" });
  }
});

EventBus.on("ui:arm_spell", ({ casterId, spellId, slotLevel: _slotLevel }) => {
  void loadSpell(spellId).then((spell) => {
    if (!spell) return;
    const caster = PartyStore.getMember(casterId);
    if (!caster) return;
    const result = spellTargeting.arm(spell, casterId);
    if (result === "armed" && spell.targeting !== "self") {
      const armedTargeting: "single" | "aoe" | "multi" | "touch" =
        spell.targeting === "aoe" || spell.targeting === "multi" || spell.targeting === "single"
          ? spell.targeting
          : "touch";
      const casterPos = inputHandler?.getPosition() ?? { x: 0, z: 0 };
      const casterFacing = inputHandler?.getFacing() ?? Direction.South;
      EventBus.emit("ui:spell_armed", {
        casterId,
        casterName: caster.name,
        spellId: spell.id,
        spellName: spell.name,
        targeting: armedTargeting,
        targetTeam: spell.targetTeam,
        rangeCells: spell.rangeCells,
        ...(spell.minTargets !== undefined && { minTargets: spell.minTargets }),
        ...(spell.maxTargets !== undefined && { maxTargets: spell.maxTargets }),
        includeDead: spell.includeDead,
        casterPos,
        casterFacing,
      });
    }
  });
});

// Esc cancels armed targeting so a misclicked Fireball can be aborted
// before burning the slot.
document.addEventListener("keydown", (e: KeyboardEvent) => {
  if (e.key !== "Escape") return;
  if (spellTargeting.isArmed()) {
    spellTargeting.cancel();
    EventBus.emit("ui:spell_disarmed", { reason: "cancelled" });
  }
});

canvas.addEventListener("click", (e: MouseEvent) => {
  const rect = canvas.getBoundingClientRect();
  const ndc = toNDC(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);

  // S23: AoE picker — when an AoE spell is armed, route ground-plane clicks
  // to commitOnCell. The high-priority entity rule (installInto) already
  // handled enemy/ally hits before we got here.
  const armed = spellTargeting.getArmed();
  if (armed && armed.spell.targeting === "aoe") {
    const cell = pickGroundCell(ndc, sceneManager.camera);
    if (cell) {
      spellTargeting.commitOnCell(cell);
      return;
    }
  }

  dispatcher.handleClick(ndc, sceneManager.camera, sceneManager.scene);
});

// ── 7. Global Escape key → open/close menu or close active dialogue (N07) ────
document.addEventListener("keydown", (e: KeyboardEvent) => {
  if (e.key !== "Escape") return;
  const snap = gameActor.getSnapshot();
  if (snap.matches("exploring") || snap.matches("combat")) {
    if (selectIsWorldFrozen(snap)) {
      // World is frozen — a dialogue or overlay is open. Close it instead of
      // opening the menu, which cannot open while frozen anyway (guard on gameMachine).
      narrativeManager.close();
    } else {
      gameActor.send({ type: "OPEN_MENU" });
    }
  } else if (snap.matches("menu")) {
    gameActor.send({ type: "CLOSE_MENU" });
  }
});

// ── U21: Hand button → attack nearest enemy ────────────────────────────────────
// The PartyBar emits ui:hand_action when a hand button is clicked. Passive hands
// (shields / "passive"-tagged items) are filtered on the React side; here we
// just forward to playerAttackNearest so exploration + combat both resolve the
// same way as a canvas click on an enemy.
EventBus.on("ui:hand_action", ({ characterId, hand }) => {
  // Downed characters take no actions — without this gate a dead healer
  // could still open her spell chooser and cure herself back up.
  const actor = PartyStore.getMember(characterId);
  if (!actor || actor.hp <= 0) return;
  // S20 — if the equipped item is a spellbook (or holy-symbol, which carries
  // the same `isSpellbook` flag), open the spell chooser instead of attacking.
  const equipped = InventoryStore.get(characterId).equipment[hand];
  if (equipped !== null && isSpellbook(equipped)) {
    EventBus.emit("ui:open_spell_chooser", { casterId: characterId, hand });
    return;
  }
  combatManager?.playerAttackNearest({ attackerInstanceId: characterId, hand });
});

// ── 8. Narrative open/close → world-freeze flag + NPC sprite state (N09) ─────
let activeNpcSpeakerId: string | null = null;
EventBus.on("narrative:start", ({ speakerInstanceId }) => {
  gameActor.send({ type: "WORLD_FREEZE" });
  if (speakerInstanceId) {
    activeNpcSpeakerId = speakerInstanceId;
    spriteManager?.setNpcState(speakerInstanceId, "talking");
  }
});
EventBus.on("narrative:ended", () => {
  gameActor.send({ type: "WORLD_UNFREEZE" });
  if (activeNpcSpeakerId) {
    spriteManager?.setNpcState(activeNpcSpeakerId, "idle");
    activeNpcSpeakerId = null;
  }
});

// NPC → hostile: flip the sprite to its hostile variant (N09).
EventBus.on("enemy:faction_flipped", ({ instanceId, next }) => {
  if (next === "hostile") spriteManager?.setNpcState(instanceId, "hostile");
});

// ── U26 / A05c: Loot pile sprite lifecycle ───────────────────────────────────
// `LootPileRenderer` swaps the legacy gold-square marker for a billboarded
// `floor.png` of the pile's headline item, with a deterministic per-instance
// mirror flip. Falls back to the procedural marker when no art is shipped.
const lootPileRenderer = new LootPileRenderer(sceneManager.scene);
EventBus.on("world:loot_dropped", ({ pileId }) => {
  const pile = LootPileStore.get(pileId);
  if (pile) lootPileRenderer.spawn(pile);
});
EventBus.on("world:item_drop_picked_up", ({ dropId }) => {
  lootPileRenderer.despawn(dropId);
});

// ── 9. Boss death → fire death knot + mark boss dead ─────────────────────────
EventBus.on("combat:entity_died", ({ instanceId }) => {
  const deathKnot = bossDeathKnots.get(instanceId);
  if (deathKnot) {
    WorldStore.setBossDead();
    narrativeManager.startKnot(deathKnot);
  }
});

// ── E17 fallback shapes ──────────────────────────────────────────────────────
// When a Tiled map references a `monster_id` that has no JSON record,
// the legacy goblin / boss placeholders keep the spawn working so test
// maps don't break. A console.warn flags the missing record so authors
// notice.

function legacyGoblinEntity(
  instanceId: string,
  monsterId: string,
  position: Position,
  faction: EnemyFaction,
  leashRadius?: number,
): MonsterEntity {
  console.warn(`[bk] no monster record for "${monsterId}" — falling back to CR 1/4 goblin stats`);
  return {
    instanceId,
    dataId: monsterId,
    kind: "monster",
    name: monsterId.charAt(0).toUpperCase() + monsterId.slice(1),
    position: { x: position.x, z: position.z },
    hp: 7,
    maxHp: 7,
    armorClass: 15,
    challengeRating: 0.25,
    speed: { walk: 30 },
    abilities: { STR: 8, DEX: 14, CON: 10, INT: 10, WIS: 8, CHA: 8 },
    spriteId: instanceId,
    faction,
    size: "medium",
    anchor: { x: position.x, z: position.z },
    leashRadius: leashRadius ?? 5,
    sight: DEFAULT_SIGHT,
    hearing: DEFAULT_HEARING,
    spells: [],
    reactions: [],
    lootTableId: null,
    xp: 50,
  };
}

function legacyBossEntity(
  instanceId: string,
  monsterId: string,
  position: Position,
  faction: EnemyFaction,
  leashRadius?: number,
): MonsterEntity {
  console.warn(`[bk] no monster record for boss "${monsterId}" — falling back to CR 2 placeholder`);
  return {
    instanceId,
    dataId: monsterId,
    kind: "monster",
    name: monsterId.charAt(0).toUpperCase() + monsterId.slice(1),
    position: { x: position.x, z: position.z },
    hp: 45,
    maxHp: 45,
    armorClass: 17,
    challengeRating: 2,
    speed: { walk: 30 },
    abilities: { STR: 14, DEX: 10, CON: 14, INT: 10, WIS: 10, CHA: 8 },
    spriteId: instanceId,
    faction,
    size: "medium",
    anchor: { x: position.x, z: position.z },
    leashRadius: leashRadius ?? 0,
    sight: DEFAULT_SIGHT,
    hearing: DEFAULT_HEARING,
    spells: [],
    reactions: [],
    lootTableId: null,
    xp: 450,
  };
}

// ── 9b. Spawner runtime (E10b) ──────────────────────────────────────────────
// When a trigger fires, resolve any spawner bound to that trigger ID and
// instantiate each monster in the list at the spawner's authored cell.
// E17: stats come from DataRegistry; falls back to a CR 1/4 goblin
// placeholder when the JSON record is absent.
EventBus.on("world:trigger_fired", ({ triggerId }) => {
  const entry = spawnerRegistry.resolveTrigger(triggerId);
  if (!entry) return;
  const cm = combatManager;
  const sm = spriteManager;
  if (!cm || !sm) return;

  // Spawners are single-use: repeat firings (click-mode levers, map
  // re-entry) must not respawn the pack. The guard persists in the
  // per-map fired-trigger set alongside the trigger's own one-shot key.
  const spawnedKey = `spawner:${triggerId}`;
  if (WorldStore.isTriggerFired(spawnedKey)) return;
  WorldStore.markTriggerFired(spawnedKey);

  const faction: "hostile" | "neutral" | "friendly" =
    entry.faction === "neutral" || entry.faction === "friendly" ? entry.faction : "hostile";

  void (async () => {
    for (const [slot, monsterId] of entry.monster_ids.entries()) {
      const instanceId = `spawn-${triggerId}-${slot}`;
      if (WorldStore.isEnemyDead(instanceId)) continue;

      const entity =
        (await buildMonsterEntity({
          instanceId,
          monsterId,
          position: entry.position,
          factionOverride: faction,
        })) ?? legacyGoblinEntity(instanceId, monsterId, entry.position, faction);

      const actor = createActor(enemyMachine, {
        input: { instanceId, position: entity.position, hp: entity.hp, maxHp: entity.maxHp, patrolPath: [], patrolIndex: 0 },
      }).start();

      sm.spawnEnemy(instanceId, monsterId, entity.position, faction, entity.size);
      cm.registerEnemy(entity, actor, null);
    }
  })();
});

// ── 11. Map loading ──────────────────────────────────────────────────────────
async function loadAndBuildMap(mapId: string): Promise<void> {
  // Build-generation guard: the launch flow can fire LOAD_MAP more than
  // once in quick succession, and every await below is a seam where a
  // newer build's synchronous teardown can interleave with this one.
  // Un-guarded, a superseded build keeps spawning into the newer build's
  // scene — ghost sprites, double enemy registrations, pack slots split
  // across manager generations (how E-pack surfaced this). Each build
  // claims a generation up front and abandons work at the next
  // checkpoint once a newer build has claimed the counter.
  const myGen = ++mapBuildGeneration;
  const isStaleBuild = () => myGen !== mapBuildGeneration;
  try {
    const dungeonMap = await getOrLoadMap(mapId);
    if (isStaleBuild()) return;
    const gridMap = new GridMap(dungeonMap);
    setGridMap(gridMap);

    // Tear down previous level
    inputHandler?.dispose();
    combatManager?.dispose();
    setCombatManager(null);
    spriteManager?.dispose();
    spriteManager = null;
    lootPileRenderer.dispose();
    // Piles are per-map; the new floor rehydrates its own set from
    // WorldStore below. Without this, stale piles from the previous
    // floor keep answering atCell/get lookups.
    LootPileStore.clear();
    doorRenderer?.dispose();
    doorRenderer = null;
    floorDecalManager?.dispose();
    floorDecalManager = null;
    propManager?.dispose();
    propManager = null;
    lightManager?.dispose();
    lightManager = null;
    narrativeManager.dispose();
    bossDeathKnots.clear();
    // W06: don't reset() — per-map state for previously visited floors must
    // survive the rebuild so backtracking restores doors / dead enemies / etc.
    WorldStore.setCurrentMap(mapId);

    // Remove old InstancedMesh/Sprite/Mesh geometry from the scene (keep lights)
    const stale = sceneManager.scene.children.filter(
      (c) =>
        c instanceof THREE.InstancedMesh ||
        c instanceof THREE.Mesh ||
        c instanceof THREE.Sprite,
    );
    for (const obj of stale) sceneManager.scene.remove(obj);

    // Build dungeon geometry
    buildDungeon(sceneManager.scene, dungeonMap);

    // Toggle the sunlight + sky background whenever the current map has
    // any outdoor cells. Each map is self-contained, so this flips on a
    // per-floor basis.
    const hasOutdoor = dungeonMap.cells.some((c) => c.noCeiling === true);
    setOutdoorLighting(hasOutdoor, dungeonMap.textureSet);
    audio.setAmbient(ambientMoodForMap(mapId));

    // W22: authored ceiling lights are built from `type: "light"` Tiled
    // objects later in this function once the cell scan has collected
    // every `LightMetadata`.

    // W18 follow-up: cross-map transitions (stairs / teleporters) carry an
    // optional target cell. When present it overrides the map's authored
    // playerStart; the game machine clears it on MAP_LOADED.
    const targetCell = gameActor.getSnapshot().context.transitionTargetCell;
    const playerStart = targetCell ?? dungeonMap.playerStart;
    const { playerStartFacing } = dungeonMap;
    cameraController.snapTo(playerStart.x, playerStart.z, playerStartFacing);

    // ── Load Ink story for this map (non-blocking; fails silently if absent) ─
    // A brand-new game (flag armed by the launch picker) plays the
    // manifest's introKnot once the story is ready. Save restores clear the
    // flag in PersistenceManager.load, so they never replay the intro.
    void narrativeManager.loadStoryForMap(mapId).then(() => {
      if (consumePendingIntro()) {
        const knot = introKnot();
        if (knot) narrativeManager.startKnot(knot);
      }
    });

    // ── Player actor ─────────────────────────────────────────────────────────
    const playerActor = createActor(playerMachine, {
      input: {
        position: playerStart,
        facing: playerStartFacing,
        hp: 30,
        maxHp: 30,
      },
    }).start();

    if (import.meta.env.DEV) {
      playerActor.subscribe((snap) => {
        console.log(`[bk] playerActor → ${JSON.stringify(snap.value)}`);
      });
    }

    // ── Party ────────────────────────────────────────────────────────────────
    // Populated by the new-game chargen flow (C13) or a save restore before
    // the game machine leaves `characterCreation`. Placement at `playerStart`
    // happens here because the party needs a spawn point for the current map.
    const party = [...PartyStore.getMembers()];
    if (party.length === 0) {
      throw new Error("Party is empty at map load — chargen flow did not populate PartyStore.");
    }
    for (const member of party) {
      member.position = playerStart;
    }

    // W09: make sure the level-up watcher can resolve this party's classes.
    warmClassRecordCache();

    // ── Sprite + Combat managers ─────────────────────────────────────────────
    spriteManager = new SpriteManager(sceneManager.scene, sceneManager.camera);
    if (import.meta.env.DEV) {
      // Companion to __bkNav: sprite render state for scripted playtests.
      (window as { __bkSprites?: () => unknown }).__bkSprites = () => spriteManager?.debugState();
    }
    const cm = new CombatManager();
    cm.registerPlayer(party, playerActor);
    cm.updatePlayerPosition(playerStart);
    cm.setScene(spriteManager, gridMap);

    // I19: enemy-death loot. The resolver runs synchronously on death, so
    // preload every item the adventure's loot tables can drop (gold is
    // synthesized, never loaded). The cache is module-level — repeat map
    // loads only fetch new ids.
    for (const id of LootTableRegistry.allItemIds()) {
      if (isStaleBuild()) return;
      if (id === GOLD_ITEM_ID || lootItemCache.has(id)) continue;
      const item = await loadItem(id);
      if (item) lootItemCache.set(id, item);
      else console.warn(`[bk] loot tables reference unknown item "${id}"`);
    }
    cm.setLootResolver(createLootTableResolver((id) => lootItemCache.get(id)));

    combatManager = cm;
    setCombatManager(cm);

    // ── Register doors from map metadata ────────────────────────────────────
    const doorCells: Array<{ position: { x: number; z: number }; doorId: string }> = [];

    // ── Trap registry (W11b) — keyed by cell for step-on dispatch ───────────
    const trapsByCell = new Map<string, TrapMetadata>();

    // ── Camp cells (W11c) — for the "good spot to camp nearby" feed hint ────
    const campCells: Array<{ x: number; z: number }> = [];
    const campsHinted = new Set<string>();

    // ── Floor-decal descriptors collected during the cell scan (W18) ────────
    const floorDecals: FloorDecalDescriptor[] = [];

    // ── Prop descriptors + solid-prop cells (W19) ───────────────────────────
    const props: PropDescriptor[] = [];
    const solidPropCells: { x: number; z: number }[] = [];

    // ── Authored ceiling lights (W22) ───────────────────────────────────────
    const lightDescriptors: LightDescriptor[] = [];

    // ── Step-trigger registries: stairs / teleporters keyed by cell (W18) ───
    type StepTransition =
      | { kind: "teleporter"; meta: TeleporterMetadata }
      | { kind: "stairs"; meta: StairsDownMetadata | StairsUpMetadata; direction: "up" | "down" }
      | { kind: "trap_teleporter"; meta: TrapTeleporterMetadata };
    const stepTransitionsByCell = new Map<string, StepTransition>();

    // ── Secret-door perception (W12) ────────────────────────────────────────
    const secretDoorPerception = new SecretDoorPerception(gridMap);

    // ── Trap perception (W18 follow-up) ─────────────────────────────────────
    const trapPerception = new TrapPerception(gridMap);

    // ── Spawner registry (E10b) — clear prior map's bindings, then rebuild ──
    spawnerRegistry.clear();
    spawnerRegistry.registerFromMap(dungeonMap);

    // ── N08b: reset NPC pickpocket index for this map ───────────────────────
    npcPickpocketInfo.clear();

    // ── E16: reset NPC dialogue index ───────────────────────────────────────
    npcDialogueInfo.clear();

    // ── I15a: reset container index for this map ───────────────────────────
    containersById.clear();

    // ── I16: reset door lock index for this map ────────────────────────────
    doorsById.clear();

    // ── W21b: reset interact indexes for this map ──────────────────────────
    interactsById.clear();
    stepInteractsByCell.clear();
    currentDungeonMap = dungeonMap;

    // ── W11c: reset the disarm rule's trap index for this map ──────────────
    trapInfoById.clear();

    // ── I07b: reset shop registry for this map ──────────────────────────────
    ShopStore.clear();

    // EV-E6: preload shop records for this map's NPCs so Ink's
    // `open_shop(<npc_id>)` finds them registered. Ids without a shop
    // file 404 and are skipped silently — most NPCs don't run shops.
    {
      const shopNpcIds = new Set<string>();
      for (const cell of dungeonMap.cells) {
        if (cell.metadata?.type === "npc") shopNpcIds.add(cell.metadata.monster_id);
      }
      if (shopNpcIds.size > 0) void loadShopsForNpcs([...shopNpcIds]);
    }

    // ── Spawn entities from map metadata ─────────────────────────────────────
    for (const cell of dungeonMap.cells) {
      // The enemy/boss/npc branches await record fetches — bail before
      // spawning anything into a scene a newer build now owns.
      if (isStaleBuild()) return;
      const meta = cell.metadata;
      if (!meta) continue;

      // ── Door registrations ───────────────────────────────────────────────
      if (meta.type === "door") {
        doorCells.push({ position: cell.position, doorId: meta.door_id });
        // I16: record lock metadata only for locked doors; unlocked doors
        // never appear in `doorsById` so the dispatcher rule short-circuits.
        if (meta.locked === true) {
          doorsById.set(meta.door_id, {
            locked: true,
            ...(meta.lock_dc !== undefined && { lock_dc: meta.lock_dc }),
            ...(meta.key_item_id !== undefined && { key_item_id: meta.key_item_id }),
          });
        }
      }

      // ── Trap registrations (W11b) — index by cell for step-on check ──────
      if (meta.type === "trap") {
        trapsByCell.set(`${cell.position.x},${cell.position.z}`, meta);
        const trapId = `trap-${meta.objectId}`;
        floorDecals.push({ kind: "trap", id: trapId, cell: cell.position, meta });
        trapInfoById.set(trapId, { meta, cell: cell.position });
        trapPerception.register({
          trapId,
          position: cell.position,
          perceptionDc: meta.perception_dc ?? Math.max(5, meta.disarm_dc - 2),
        });
      }

      // ── Camp cells (W11c) — campfire decal + nearby-rest hint index ──────
      if (meta.type === "camp") {
        floorDecals.push({ kind: "camp", id: `camp-${meta.objectId}`, cell: cell.position });
        campCells.push(cell.position);
      }

      // ── Teleporter / stairs decals + step-trigger index (W18) ────────────
      if (meta.type === "teleporter") {
        floorDecals.push({
          kind: "teleporter",
          id: meta.teleporter_id,
          cell: cell.position,
          meta,
        });
        stepTransitionsByCell.set(`${cell.position.x},${cell.position.z}`, {
          kind: "teleporter",
          meta,
        });
      }
      if (meta.type === "stairs_down") {
        floorDecals.push({
          kind: "stairs",
          id: `stairs-down-${meta.objectId}`,
          cell: cell.position,
          direction: "down",
          meta,
        });
        stepTransitionsByCell.set(`${cell.position.x},${cell.position.z}`, {
          kind: "stairs",
          meta,
          direction: "down",
        });
      }
      if (meta.type === "stairs_up") {
        floorDecals.push({
          kind: "stairs",
          id: `stairs-up-${meta.objectId}`,
          cell: cell.position,
          direction: "up",
          meta,
        });
        stepTransitionsByCell.set(`${cell.position.x},${cell.position.z}`, {
          kind: "stairs",
          meta,
          direction: "up",
        });
      }
      if (meta.type === "trap_teleporter") {
        stepTransitionsByCell.set(`${cell.position.x},${cell.position.z}`, {
          kind: "trap_teleporter",
          meta,
        });
      }

      // ── Free-standing props / containers (W19) ───────────────────────────
      if (meta.type === "prop") {
        props.push({ kind: "prop", cell: cell.position, meta });
        if (meta.blocks_movement) solidPropCells.push(cell.position);
      }
      // ── Floor items (I18) — authored `item` objects become loot piles ────
      // One-shot per map: the fired-key marks "this authored item has been
      // materialized"; the pile itself then lives (and persists) in
      // WorldStore.floorItemDrops until picked up, so re-entry restores
      // uncollected items without duplicating collected ones.
      if (meta.type === "item") {
        const itemKey = `item:${meta.objectId}`;
        if (!WorldStore.isTriggerFired(itemKey)) {
          WorldStore.markTriggerFired(itemKey);
          WorldStore.addFloorDrop(`item-${meta.objectId}`, cell.position, [meta.item_id]);
        }
      }

      if (meta.type === "container") {
        props.push({ kind: "container", cell: cell.position, meta });
        // Containers always occupy their cell.
        solidPropCells.push(cell.position);
        // I15a: index by container_id for the interact rule.
        containersById.set(meta.container_id, {
          cell: cell.position,
          locked: meta.locked ?? false,
          ...(meta.loot_table !== undefined && { loot_table: meta.loot_table }),
          ...(meta.lock_dc !== undefined && { lock_dc: meta.lock_dc }),
          ...(meta.key_item_id !== undefined && { key_item_id: meta.key_item_id }),
        });
      }

      // ── Interact objects (W21b) — lever / button / plate ────────────────
      if (meta.type === "interact") {
        props.push({ kind: "interact", cell: cell.position, meta });
        interactsById.set(meta.interact_id, { cell: cell.position, meta });
        if (meta.trigger_mode === "step") {
          stepInteractsByCell.set(`${cell.position.x},${cell.position.z}`, { meta });
        }
      }

      // ── Ceiling lights (W22) ─────────────────────────────────────────────
      if (meta.type === "light") {
        lightDescriptors.push({ cell: cell.position, meta });
      }

      // ── Secret-door registrations (W12) ──────────────────────────────────
      if (meta.type === "secret_door") {
        const doorId = meta.door_id ?? `secret-${meta.objectId}`;
        secretDoorPerception.register({
          doorId,
          position: cell.position,
          dc: meta.dc,
        });
      }

      // ── Enemy spawns ─────────────────────────────────────────────────────
      if (meta.type === "enemy") {
        const monsterId = meta.monster_id;
        // E-pack: `count` packs 2–4 small monsters into the spawn cell.
        // Members get suffixed instanceIds (`enemy-7`, `enemy-7-p2`, …) so
        // W06 death tracking stays per-member. Size gating happens after
        // the first member's record resolves — non-smalls force count 1.
        const authoredCount = Math.max(1, Math.min(4, Math.trunc(meta.count ?? 1)));
        for (let packIdx = 0; packIdx < authoredCount; packIdx++) {
          const instanceId =
            packIdx === 0 ? `enemy-${meta.objectId}` : `enemy-${meta.objectId}-p${packIdx + 1}`;
          // W06: skip enemies already killed on this map.
          if (WorldStore.isEnemyDead(instanceId)) continue;

          // E17: pull authored stats from DataRegistry. Pre-E17 every enemy
          // spawned with hardcoded HP 7 / AC 15 / goblin abilities — a mind
          // flayer with HP 71 in JSON entered combat with 7 HP. Falls back
          // to the legacy goblin defaults when the JSON record is absent so
          // test maps with fictional ids still load.
          const factionOverride: EnemyFaction =
            meta.faction === "neutral" || meta.faction === "friendly" ? meta.faction : "hostile";
          // E-pack: packed spawns hold formation — leash 0 pins members to
          // their spawn cell's 2×2 corners until the party is detected
          // (EoB2 packs stood their ground; independent patrols would
          // scatter the pack within seconds of map load).
          const leashOverride = authoredCount > 1 ? 0 : meta.leash_radius;
          const entity =
            (await buildMonsterEntity({
              instanceId,
              monsterId,
              position: cell.position,
              factionOverride,
              ...(leashOverride !== undefined && { leashRadiusOverride: leashOverride }),
            })) ?? legacyGoblinEntity(instanceId, monsterId, cell.position, factionOverride, leashOverride);

          if (authoredCount > 1 && entity.size !== "small" && packIdx === 0) {
            console.warn(
              `[spawn] enemy #${meta.objectId} (${monsterId}) authors count=${authoredCount} but size is "${entity.size}" — only small monsters pack; spawning 1`,
            );
          }

          const actor = createActor(enemyMachine, {
            input: { instanceId, position: entity.position, hp: entity.hp, maxHp: entity.maxHp, patrolPath: [], patrolIndex: 0 },
          }).start();

          spriteManager.spawnEnemy(instanceId, monsterId, entity.position, entity.faction, entity.size);
          cm.registerEnemy(entity, actor, null);
          if (entity.size !== "small") break;
        }
      }

      // ── Boss spawns ──────────────────────────────────────────────────────
      if (meta.type === "boss") {
        const monsterId = meta.monster_id;
        const instanceId = `boss-${meta.objectId}`;
        const deathKnot = meta.death_knot;
        // W06: dead bosses don't respawn on re-entry.
        if (WorldStore.isEnemyDead(instanceId)) continue;

        // E17: pull boss stats from DataRegistry like regular enemies.
        // Falls back to the legacy CR-2 placeholder when the JSON is
        // missing, so old test maps still load.
        const factionOverride: EnemyFaction =
          meta.faction === "neutral" || meta.faction === "friendly" ? meta.faction : "hostile";
        const entity =
          (await buildMonsterEntity({
            instanceId,
            monsterId,
            position: cell.position,
            factionOverride,
            ...(meta.leash_radius !== undefined && { leashRadiusOverride: meta.leash_radius }),
          })) ?? legacyBossEntity(instanceId, monsterId, cell.position, factionOverride, meta.leash_radius);

        const actor = createActor(enemyMachine, {
          input: { instanceId, position: entity.position, hp: entity.hp, maxHp: entity.maxHp, patrolPath: [], patrolIndex: 0 },
        }).start();

        spriteManager.spawnEnemy(instanceId, monsterId, entity.position, entity.faction, entity.size);
        cm.registerEnemy(entity, actor, null);

        if (deathKnot) {
          bossDeathKnots.set(instanceId, deathKnot);
        }
      }

      // ── NPC spawns ───────────────────────────────────────────────────────
      if (meta.type === "npc") {
        const npcId = meta.monster_id;
        const instanceId = `npc-${meta.objectId}`;
        const inkKnot = meta.ink_knot;
        const portrait = meta.portrait;

        if (!inkKnot) continue;

        const _entity: NPCEntity = {
          instanceId,
          dataId: npcId,
          kind: "npc",
          name: npcId.charAt(0).toUpperCase() + npcId.slice(1),
          position: { x: cell.position.x, z: cell.position.z },
          hp: 1,
          maxHp: 1,
          armorClass: 10,
          speed: { walk: 0 },
          abilities: { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 },
          inkKnot,
          spriteId: instanceId,
        };

        spriteManager.spawnNpc(instanceId, npcId, cell.position, inkKnot, portrait);

        npcDialogueInfo.set(instanceId, {
          inkKnot,
          ...(portrait !== undefined && { portrait }),
        });

        // N08b: if this NPC is pickpocketable, remember the DC + loot table.
        if (meta.pickpocketDC !== undefined) {
          npcPickpocketInfo.set(instanceId, {
            dc: meta.pickpocketDC,
            ...(meta.pickpocketTable !== undefined && { table: meta.pickpocketTable }),
          });
        }
      }
    }

    gridMap.registerDoors(doorCells);
    gridMap.registerSolidProps(solidPropCells);
    doorRenderer = new DoorRenderer(sceneManager.scene, gridMap, doorCells);
    floorDecalManager = new FloorDecalManager(sceneManager.scene, floorDecals);
    propManager = new PropManager(sceneManager.scene);
    for (const descriptor of props) propManager.spawn(descriptor);

    // ── Rehydrate floor-drop piles (I18) ─────────────────────────────────────
    // Everything in WorldStore.floorItemDrops — authored `item` objects
    // materialized above, plus uncollected combat/container drops from a
    // previous visit — gets a live pile + billboard. Item records load
    // async; unknown ids are dropped with a warn so a typo'd map doesn't
    // brick the floor.
    for (const drop of WorldStore.getFloorDrops()) {
      if (isStaleBuild()) return;
      const counts = new Map<string, number>();
      for (const id of drop.items) counts.set(id, (counts.get(id) ?? 0) + 1);
      const stacks: ItemStack[] = [];
      for (const [id, qty] of counts) {
        const item = id === GOLD_ITEM_ID ? makeGoldItem() : await loadItem(id);
        if (item) stacks.push({ item, quantity: qty });
        else console.warn(`[bk] floor drop "${drop.dropId}" references unknown item "${id}"`);
      }
      if (stacks.length === 0) continue;
      const pile: LootPile = { id: drop.dropId, position: drop.cell, stacks };
      LootPileStore.put(pile);
      lootPileRenderer.spawn(pile);
    }

    // W22: build the authored ceiling lights now that every `LightMetadata`
    // has been collected. `LightManager` seeds `lightsOff` from
    // `initial_state`, soft-caps at 8 by farthest-from-player distance,
    // and registers invisible raycast targets for direct-click toggling.
    lightManager = new LightManager(
      sceneManager.scene,
      lightDescriptors,
      () => currentPlayerCell,
    );
    // Register a per-light observed variable (`light_<id>_off`) with the
    // narrative layer so Ink can gate on any authored light's state.
    for (const lightId of lightManager.lightIds()) {
      narrativeManager.registerObservedVariable(`light_${lightId}_off`);
    }

    // ── Notify player machine when camera tween finishes ────────────────────
    cameraController.setOnTweenComplete(() => {
      playerActor.send({ type: "ANIMATION_COMPLETE" });
    });

    // ── Input handler ────────────────────────────────────────────────────────
    inputHandler = new InputHandler(
      playerStart,
      playerStartFacing,
      gridMap,
      cameraController,
      playerActor,
      (pos) => {
        currentPlayerCell = { x: pos.x, z: pos.z };
        cm.updatePlayerPosition(pos);
        narrativeManager.checkPlayerMove(pos, dungeonMap);

        // Mark explored and notify minimap
        WorldStore.markExplored(pos);
        EventBus.emit("ui:player_moved", { position: pos });
        noiseBroadcast.emitNoise(pos, NOISE_RADIUS.movement);

        // GF2/GF3: footstep per step; nearby unseen hostiles get an
        // occasional low growl (throttled inside the manager) — the party
        // hears trouble before it sees it, EOB2 style.
        audio.play("step");
        if (!gameActor.getSnapshot().matches("combat")) {
          let nearest = Infinity;
          for (const e of cm.getCampEntities()) {
            if (e.faction !== "hostile" || e.alive === false) continue;
            const d = chebyshev(pos, e.position);
            if (d < nearest) nearest = d;
          }
          if (nearest >= 2 && nearest <= 6) audio.threatNear(nearest);
        }

        // W12: passive Perception against any nearby hidden secret doors.
        secretDoorPerception.checkOnMove(pos, party);

        // W18: passive Perception against any nearby hidden traps. W11c:
        // a successful spot gets a feed line naming the sharp-eyed member —
        // a red ring quietly appearing on the floor was too easy to miss.
        const spottedTraps = trapPerception.checkOnMove(pos, party);
        if (spottedTraps.length > 0) {
          const spotter = highestPassivePerceiver(party);
          for (const spottedId of spottedTraps) {
            const spotted = trapInfoById.get(spottedId);
            pushFeed(
              `${spotter?.name ?? "Someone"} spots a trap ahead${spotted ? ` — ${spotted.meta.name}` : ""}!`,
            );
          }
        }

        // W11c: rest-spot hint — the campfire decal is visible in-world, but
        // call it out once per visit so nobody walks past their long rest.
        for (const campCell of campCells) {
          const campKey = `${campCell.x},${campCell.z}`;
          if (campsHinted.has(campKey)) continue;
          if (chebyshev(pos, campCell) > 2) continue;
          if (!hasLineOfSight(gridMap, pos, campCell)) continue;
          campsHinted.add(campKey);
          pushFeed("A sheltered spot nearby would make a good camp.");
        }

        // W11b: step-on trap dispatch. One-shot per map via firedTriggers
        // (keyed as `trap:<objectId>` to stay out of Ink's trigger namespace).
        // W11c: a disarmed trap is safe to walk over.
        const trapMeta = trapsByCell.get(`${pos.x},${pos.z}`);
        if (trapMeta) {
          const trapId = `trap-${trapMeta.objectId}`;
          const trapKey = `trap:${trapMeta.objectId}`;
          const frontRow = party[0];
          // W18: stepping on a trap reveals it (renders the decal) before firing.
          WorldStore.markTrapRevealed(trapId, pos);
          if (
            !WorldStore.isTriggerFired(trapKey) &&
            !WorldStore.isTrapDisarmed(trapId) &&
            frontRow
          ) {
            detonateTrap(trapMeta, trapId, pos, frontRow, "step_on");
          }
        }

        // W21b: pressure plates — step-on interact objects route through the
        // same effect router as the click rule. One-shot per map via
        // firedTriggers (keyed `interact:<id>`) so a plate doesn't refire
        // every time the party stands on it.
        const stepInteract = stepInteractsByCell.get(`${pos.x},${pos.z}`);
        if (stepInteract && currentDungeonMap) {
          const interactId = stepInteract.meta.interact_id;
          const stepKey = `interact:${interactId}`;
          if (!WorldStore.isTriggerFired(stepKey)) {
            WorldStore.markTriggerFired(stepKey);
            const result = routeInteractEffect(stepInteract.meta.effect, currentDungeonMap);
            dispatchInteractDirective(result, pos, interactId, currentDungeonMap.id);
          }
        }

        // W18: step-on transitions — teleporter / stairs / trap_teleporter.
        const transition = stepTransitionsByCell.get(`${pos.x},${pos.z}`);
        if (transition) {
          const targetCell = parseTargetCell(transition.meta.target_cell);
          const targetMap = transition.meta.target_map;
          const sameMap = !targetMap || targetMap === mapId;
          if (transition.kind === "teleporter") {
            EventBus.emit("world:teleporter_used", {
              teleporterId: transition.meta.teleporter_id,
              fromCell: pos,
              targetMap,
              targetCell,
            });
          } else if (transition.kind === "stairs") {
            EventBus.emit("world:stairs_used", {
              stairsId: `stairs-${transition.direction}-${transition.meta.objectId}`,
              direction: transition.direction,
              targetMap,
              targetCell,
            });
          }
          if (sameMap) {
            cameraController.snapTo(targetCell.x, targetCell.z, playerStartFacing);
            currentPlayerCell = targetCell;
            cm.updatePlayerPosition(targetCell);
            EventBus.emit("ui:player_moved", { position: targetCell });
            WorldStore.markExplored(targetCell);
          } else {
            // Cross-map: hand off to the game machine, carrying the
            // authored target cell so the new build spawns the party there.
            gameActor.send({ type: "LOAD_MAP", mapId: targetMap, targetCell });
          }
        }

        // C08: camp cells — prompt for a long rest when no hostiles are near.
        const posMeta = dungeonMap.cells[pos.z * dungeonMap.width + pos.x]?.metadata;
        if (posMeta?.type === "camp") {
          const eligibility = checkCampEligibility(pos, cm.getCampEntities());
          if (eligibility.ok) {
            EventBus.emit("ui:camp_prompt", { cell: pos });
          } else {
            pushFeed("Too dangerous to make camp — something hostile is close.");
          }
        }

        // ── Exit trigger check ─────────────────────────────────────────────
        for (const cell of dungeonMap.cells) {
          const meta = cell.metadata;
          if (meta?.type !== "exit") continue;
          if (cell.position.x !== pos.x || cell.position.z !== pos.z) continue;

          if (meta.requires_boss_dead && !WorldStore.isBossDead()) continue;

          // Victory!
          gameActor.send({ type: "PLAYER_VICTORY" });
          narrativeManager.startKnot("victory");
        }
      },
    );

    // Emit initial player position so MiniMap and PartyBar are populated
    currentPlayerCell = { x: playerStart.x, z: playerStart.z };
    WorldStore.markExplored(playerStart);
    EventBus.emit("ui:player_moved", { position: playerStart });
    EventBus.emit("ui:party_update", {});
    EventBus.emit("world:map_name_changed", {
      displayName: dungeonMap.displayName ?? "",
      width: dungeonMap.width,
      height: dungeonMap.height,
    });

    gameActor.send({ type: "MAP_LOADED" });
  } catch (err) {
    console.error("[bk] loadAndBuildMap failed", err);
    const message = err instanceof Error ? err.message : String(err);
    gameActor.send({ type: "LOAD_FAILED", error: message });
  }
}

// ── 11b. Save-input provider + autosave + quicksave hotkeys ──────────────────
// Builds a `SaveSlotInput` snapshot from the live refs (input handler,
// narrative manager, current map). The module-singleton
// `setSaveInputProvider` hands the same function across the engine/UI
// boundary so MainMenu can surface the Save / Load entry without prop
// threading.
function currentSaveInput(): SaveSlotInput {
  const snap = gameActor.getSnapshot();
  const mapId = snap.context.currentMapId ?? "";
  const dm = currentDungeonMap;
  const pos = inputHandler?.getPosition() ?? dm?.playerStart ?? { x: 0, z: 0 };
  const facing = inputHandler?.getFacing() ?? dm?.playerStartFacing ?? Direction.South;
  return {
    floorName: dm?.displayName ?? mapId,
    pointer: { mapId, position: pos, facing },
    inkState: narrativeManager.getInkState(),
  };
}

setSaveInputProvider(currentSaveInput);

createAutosaver({
  inputProvider: currentSaveInput,
  onError: (err, reason) => {
    console.warn(`[autosave:${reason}]`, err);
  },
});

createQuicksaveHotkeys({
  inputProvider: currentSaveInput,
  isBlocked: () => isSaveBlocked(),
  blockedReason: () => getSaveBlockReason() ?? "not right now",
  onLoadSuccess: (result) => {
    // restoreStores() repopulated PartyStore / WorldStore / SessionStore;
    // the game machine drives the scene rebuild via LOAD_MAP.
    gameActor.send({ type: "LOAD_MAP", mapId: result.file.state.pointer.mapId });
  },
});

// ── 12. Subscribe to game machine for map-load triggers ─────────────────────
gameActor.subscribe((snapshot) => {
  if (snapshot.matches("loading") && snapshot.context.currentMapId) {
    void loadAndBuildMap(snapshot.context.currentMapId);
  }
});

// ── 12a. Quit-to-title teardown ─────────────────────────────────────────────
// When the pause menu sends SHOW_LAUNCH, the game machine transitions back
// to `launch`. Dispose per-level managers and clear scene children so the
// InputHandler stops swallowing WASD behind the LaunchScreen and
// SpriteManager stops ticking an invisible dungeon. Stores are not reset
// here — the LaunchAdventurePicker / load flow repopulates them, and
// `loadAndBuildMap` already tears down again on the next LOAD_MAP.
let inLaunch = gameActor.getSnapshot().matches("launch");
gameActor.subscribe((snapshot) => {
  const nowLaunch = snapshot.matches("launch");
  if (nowLaunch && !inLaunch) {
    inputHandler?.dispose();
    inputHandler = null;
    combatManager?.dispose();
    setCombatManager(null);
    setGridMap(null);
    combatManager = null;
    spriteManager?.dispose();
    spriteManager = null;
    lootPileRenderer.dispose();
    LootPileStore.clear();
    doorRenderer?.dispose();
    doorRenderer = null;
    floorDecalManager?.dispose();
    floorDecalManager = null;
    propManager?.dispose();
    propManager = null;
    lightManager?.dispose();
    lightManager = null;
    narrativeManager.dispose();
    bossDeathKnots.clear();
    spawnerRegistry.clear();
    npcPickpocketInfo.clear();
    npcDialogueInfo.clear();

    const stale = sceneManager.scene.children.filter(
      (c) =>
        c instanceof THREE.InstancedMesh ||
        c instanceof THREE.Mesh ||
        c instanceof THREE.Sprite,
    );
    for (const obj of stale) sceneManager.scene.remove(obj);

    currentDungeonMap = null;
  }
  inLaunch = nowLaunch;
});

// ── 12b. Authoring live reload (AS31) ──────────────────────────────────────
// Dev-only: connect to the authoring server's WebSocket so save:done
// events on the current map's .tmj / ink / data files trigger a
// LOAD_MAP rebuild. No-op when the authoring server isn't running
// (the module reconnects quietly in the background).
if (import.meta.env.DEV) {
  startAuthoringLiveReload();
}

// ── 13. Launch gate (P19, P20) ──────────────────────────────────────────────
// Boot no longer mounts an adventure at module-load time. The game machine
// starts in `launch`; the launch/save/adventure pickers orchestrate boot
// via `bootAdventure()` in `src/engine/adventure/bootAdventure.ts`, then
// dispatch `LOAD_MAP` to this actor once an adventure is chosen and the
// party is ready. The `gameActor.subscribe` above is all main.tsx needs —
// it picks up `LOAD_MAP` the moment the machine enters `loading`.
